import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, Button, Slider, Label, Bar } from "@ui5/webcomponents-react";
import { getCroppedImg } from "../../utils/cropImage";

/**
 * ImageCropModal - LinkedIn-style image cropping modal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {string} props.imageSrc - The source image data URL
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onCropComplete - Callback with cropped image blob
 */
const ImageCropModal = ({ open, imageSrc, onClose, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Called when crop area changes
  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  // Called when zoom changes
  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  // Called when crop is complete (debounced)
  const onCropCompleteCallback = useCallback(
    (croppedArea, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  // Handle save button click
  const handleSave = async () => {
    try {
      setIsProcessing(true);
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImageBlob);
      handleClose();
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Failed to crop image. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel button click
  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      headerText="Crop Profile Picture"
      style={{
        "--_ui5_dialog_resize_handle_color": "transparent",
        width: "700px",
        maxWidth: "90vw",
        maxHeight: "85vh",
      }}
      footer={
        <Bar
          endContent={
            <>
              <Button
                design="Transparent"
                onClick={handleClose}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                design="Emphasized"
                onClick={handleSave}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Save"}
              </Button>
            </>
          }
        />
      }
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Crop Area */}
        <div className="relative w-full h-[450px] bg-gray-100 rounded-lg overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1} // 1:1 aspect ratio for profile pictures
            cropShape="rect" // Use 'round' for circular crop
            showGrid={true}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        {/* Zoom Control */}
        <div className="flex items-center gap-4 px-1">
          <span className="text-sm font-medium text-gray-700 min-w-[50px]">
            Zoom:
          </span>
          <span className="text-sm text-gray-500">1x</span>
          <Slider
            min={1}
            max={2}
            step={0.1}
            value={zoom}
            onInput={(e) => setZoom(e.target.value)}
            className="flex-1"
          />
          <span className="text-sm text-gray-500">2x</span>
        </div>

        {/* Instructions */}
        <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
          <p>
            <strong>Tip:</strong> Drag the image to reposition • Use the slider
            to zoom
          </p>
        </div>
      </div>
    </Dialog>
  );
};

export default ImageCropModal;
